import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import PreviewRoleContext from '../../context/PreviewRoleContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
function getToken() { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; } }
function apiFetch(path) { return fetch(`${API_BASE}${path}`, { headers: { 'x-worker-token': getToken() } }).then(r => r.ok ? r.json() : Promise.reject()); }

// Office (Zone 1) workflow boards — reuse the Workflows board via /admin/workflows/:type.
const WORKFLOW_CARDS = [
  { key: 'contract',     type: 'contract',     label: 'Contract',     path: '/admin/workflows/contract',     icon: '📄', color: '#1e3a5f', desc: 'Contract status across active jobs' },
  { key: 'subcontracts', type: 'subcontracts', label: 'Subcontracts', path: '/admin/workflows/subcontracts', icon: '🤝', color: '#4c1d95', desc: 'Sub agreements across active jobs' },

  { key: 'submittals',   type: 'submittals',   label: 'Submittals',   path: '/admin/workflows/submittals',   icon: '📋', color: '#1e3a5f', desc: 'Submittal approvals across jobs' },
  { key: 'wo_billing',   type: null,           label: 'Work Order Billing', path: '/admin/wo-billing', icon: '💰', color: '#15803d', desc: 'Finished work orders, prepared, checked, sent and closed' },
  { key: 'lj_billing',   type: null,           label: 'Large Job Billing',  path: '/admin/large-billing', icon: '🏗️', color: '#1e3a5f', desc: 'Pay applications and invoices on large jobs, prepared, checked, sent and paid' },
];
// Existing pages moved under Administration.
const LINK_CARDS = [
  { key: 'po',         label: 'Purchase Orders',    path: '/admin/purchase-orders', icon: '🧾', color: '#7c2d12', desc: 'POs issued to vendors' },
  { key: 'invoices',   label: 'Invoice Tracking',   path: '/admin/invoices',        icon: '📒', color: '#15803d', desc: 'Every invoice — search and sort by PM, customer, job; add the Foundation invoice #' },
  // Still the same board at /admin/workflows/payroll — it keeps its `type` so the
  // count on the tile carries over with it.
  { key: 'payroll',    type: 'payroll', label: 'Certified Payroll', path: '/admin/workflows/payroll', icon: '💵', color: '#a16207', desc: 'Certified payroll status across jobs' },
  { key: 'timesheets', label: 'Timesheets',         path: '/admin/timesheets',      icon: '⏱️', color: '#065f46', desc: 'Crew hours & approvals' },
  { key: 'cards',      label: 'Credit Cards & Reimbursements', path: '/admin/card-charges', icon: '💳', color: '#0e7490', desc: 'Charges and out-of-pocket spend, by job or by person' },
];
// Company directories (moved here from Settings).
const DIRECTORY_CARDS = [
  // Opens the Employees area (Employee List, Time Requested Off, …) — owner, Executives
  // and the office only; PMs don't get the tile and the routes turn them away.
  { key: 'employees', label: 'Employees', path: '/admin/employees', icon: '👷', color: '#1e3a5f', desc: 'Employee list, time requested off & more', countPath: '/users', roles: ['admin', 'Executive', 'Accounting'] },
  { key: 'vendors',   label: 'Vendors',   path: '/admin/vendors',   icon: '🚚', color: '#7c2d12', desc: 'Suppliers & subcontractors',        countPath: '/vendors' },
  { key: 'customers', label: 'Customers', path: '/admin/clients',   icon: '🏢', color: '#4c1d95', desc: 'Clients & general contractors',      countPath: '/clients' },
  // A licence is a record you keep, not a workflow you run — it belongs with the
  // other things the office holds on file.
  { key: 'permits',   label: 'Permits & Licenses', path: '/admin/permits', icon: '📜', color: '#a16207', desc: 'Renewals, expirations & filings' },
];

// Every page under Administration, flat, for the phone's "Desktop pages" tile (Ryan,
// Oct 1 2026). Asks is listed for Executives; the hub itself asks the server for others.
export const ADMIN_PAGES = [
  ...WORKFLOW_CARDS, ...LINK_CARDS, ...DIRECTORY_CARDS,
  { key: 'asks', label: 'Asks', path: '/admin/send-asks', icon: '📣', color: '#0f766e', roles: ['admin', 'Executive'] },
].map(c => ({ key: c.key, label: c.label, path: c.path, icon: c.icon, color: c.color, roles: c.roles || null }));

export default function AdminAdministration() {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({});
  const [dirCounts, setDirCounts] = useState({});
  const previewRole = useContext(PreviewRoleContext);   // set while an Executive is "viewing as" someone
  const role = previewRole || (() => { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.role || 'admin'; } catch { return 'admin'; } })();
  // Asks: only for the people an Executive has handed it to (and Executives themselves).
  const [canSendAsks, setCanSendAsks] = useState(false);
  useEffect(() => { apiFetch('/asks/access').then(a => setCanSendAsks(!!a?.can_send)).catch(() => {}); }, []);
  const directoryCards = DIRECTORY_CARDS.filter(s => !s.roles || s.roles.includes(role));

  useEffect(() => {
    apiFetch('/workflows').then(w => {
      const c = {}; if (Array.isArray(w)) w.forEach(x => { c[x.type] = (c[x.type] || 0) + 1; });
      setCounts(c);
    }).catch(() => {});
    DIRECTORY_CARDS.forEach(s => apiFetch(s.countPath)
      .then(d => setDirCounts(c => ({ ...c, [s.key]: Array.isArray(d) ? d.length : null })))
      .catch(() => {}));
  }, []);

  const card = (s, count, unit = 'active') => (
    <button key={s.key} onClick={() => navigate(s.path)}
      style={{ textAlign: 'left', background: '#fff', border: '1px solid var(--border)', borderTop: `4px solid ${s.color}`, borderRadius: 12, padding: 20, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 6, transition: 'box-shadow .15s, transform .1s' }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
      <div style={{ fontSize: 30 }}>{s.icon}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{s.label}</div>
      <div style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.4 }}>{s.desc}</div>
      {count != null && <div style={{ fontSize: 12, color: s.color, fontWeight: 700, marginTop: 2 }}>{count} {unit}</div>}
    </button>
  );

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Administration</h1>
          <p className="admin-page-sub">Office workflows, purchasing &amp; payroll</p>
        </div>
      </div>
      <div style={{ marginBottom: 10, fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>Office Workflows (Zone 1)</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16, marginBottom: 28 }}>
        {WORKFLOW_CARDS.map(s => card(s, counts[s.type]))}
      </div>
      <div style={{ marginBottom: 10, fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>Purchasing &amp; Payroll</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
        {LINK_CARDS.map(s => card(s, s.type ? counts[s.type] : null))}
      </div>

      <div style={{ marginTop: 28, marginBottom: 10, fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>Company Directories</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
        {directoryCards.map(s => card(s, dirCounts[s.key], 'records'))}
      </div>

      {canSendAsks && (<>
        <div style={{ marginTop: 28, marginBottom: 10, fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>Company</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
          {card({ key: 'asks', label: 'Asks', path: '/admin/send-asks', icon: '📣', color: '#0f766e', desc: 'RSVPs, questions & notes to acknowledge — sent to everyone, a group or picked people' })}
        </div>
      </>)}
    </AdminLayout>
  );
}
